import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'

import { useOutsideClick } from 'shared/hooks/useOutsideClick'
import { languagesType, languagesWithIcons, shortLanguages } from 'shared/locale'

import { actions } from 'features/userSettings'
import { selectLanguage } from 'features/userSettings/selectors'

import { ReactComponent as ArrowSVG } from './img/arrow.svg'

import './SetLanguage.scss'

interface ISetLanguageProps {
  isColumn?: boolean
}

const b = block('select-lang')

const SetLanguage: React.FC<ISetLanguageProps> = ({ isColumn = false }) => {
  const dispatch = useDispatch()

  const lang = useSelector(selectLanguage)
  const setLangRef = useRef(null)

  const changeLang = useCallback(newLang => {
    dispatch(actions.changeLang(newLang))
  }, [])

  const [isOpen, setOpened] = useState(false)
  const [timeoutId, setTimeoutId] = useState(0)

  useOutsideClick(setLangRef, () => setOpened(false))

  const items = useMemo(
    () =>
      Object.values(languagesWithIcons)
        .map(item => {
          const isCurrent = lang === item.lang
          if (isCurrent) {
            return null
          }

          return (
            <button
              key={item.lang}
              className={b('item')}
              onClick={() => changeLang(item.lang)}
              type="button"
            >
              <div className={b('item-left')}>
                <img src={item.icon} alt="" />
                <span>{shortLanguages[item.lang as languagesType]}</span>
              </div>
            </button>
          )
        })
        .filter(temp => temp),
    [lang, changeLang],
  )

  const openedSetTimeout = useCallback(
    time => {
      if (isOpen) {
        const newTimeout = setTimeout(() => {
          setOpened(false)
          setTimeoutId(0)
        }, time)
        setTimeoutId(+newTimeout)
      }
    },
    [isOpen],
  )

  useEffect(() => openedSetTimeout(6000), [isOpen])

  useEffect(
    () => () => {
      if (timeoutId) clearTimeout(timeoutId)
    },
    [timeoutId],
  )

  const onSectionClick = useCallback(() => {
    setOpened(!isOpen)
    if (!isOpen) openedSetTimeout(2500)
  }, [isOpen, openedSetTimeout])

  const onMouseLeave = useCallback(() => {
    openedSetTimeout(2500)
  }, [openedSetTimeout])

  const onMouseEnter = useCallback(() => {
    if (timeoutId) clearTimeout(timeoutId)
  }, [timeoutId])

  return (
    <div className={b()} onClick={onSectionClick} onMouseLeave={onMouseLeave} ref={setLangRef}>
      <div className={b('top', { open: isOpen })}>
        <div className={b('top-left')}>
          <img alt="" src={languagesWithIcons[lang].icon} />
          <span>{shortLanguages[lang]}</span>
        </div>
        <ArrowSVG className={b('arrow', { open: isOpen })} />
      </div>
      <div className={b('items', { open: isOpen, isColumn })} onMouseEnter={onMouseEnter}>
        {items}
      </div>
    </div>
  )
}

export default SetLanguage
